import { AxiosError } from "axios";
import cookie from "react-cookies";

import { NotificationManagerAPI, useNotificationManager } from "notifications/NotificationManager";
import { circuitBreaker } from "circuitbreaker/CircuitBreaker";
import { isLoggedIn } from "services/AccountServices";
import { navigate } from "utility/NavigationService";

// ---==============================================---
//     KarunaCLOUD Universal Response Error Handler
// ---==============================================---

export const KarunaCLOUD_parseAxiosError = (error: AxiosError): [string, number, string, string] => {
    let url = error?.response?.request?.responseURL || error?.request?.responseURL || null;
    let status = error?.response?.status || error?.status || null;
    let requestData = "";
    let reason = error?.message || null;
    return [url, status, requestData, reason];
};

/**
 * Handles circuit breaker failure recording and associated actions
 * @param notificationManager - The notification manager instance
 * @param userLoggedIn - Whether the user is currently logged in
 * @param errorMessage - The error message to display to the user
 * @param logContext - Context string for logging purposes
 * @returns false (indicating the request failed)
 */
const handleCircuitBreakerFailure = (
    notificationManager: NotificationManagerAPI | null,
    userLoggedIn: boolean,
    errorMessage: string,
    logContext: string
): boolean => {
    const shouldShowError = circuitBreaker.recordFailure();

    // Navigate to service unavailable page when circuit breaker trips
    if (shouldShowError && userLoggedIn && window.location.pathname !== "/serviceunavailable") {
        console.log(`[KarunaCloudServices] Circuit breaker tripped (${logContext}) - navigating to service unavailable page`);
        void navigate("/serviceunavailable");
        return false;
    }

    // Show notification for logged-in users when circuit breaker trips
    if (shouldShowError && notificationManager && userLoggedIn) {
        const notificationId = notificationManager.addError(errorMessage);
        circuitBreaker.recordServerUnavailableNotification(notificationId);
    }
    else if (!userLoggedIn) {
        console.log(`[KarunaCloudServices] User not logged in - skipping standard connection error notification (${logContext})`);
    }

    return false;
};

// Keep the original function for backward compatibility (without notifications)
export const KarunaCLOUD_responseValidator = (response: any, notificationManager: NotificationManagerAPI | null = null): boolean => {
    // Check if user is logged in - if not, let the Login page handle its own error notifications
    const userLoggedIn = isLoggedIn();

    // Check circuit breaker before processing - now pass notificationManager
    if (!circuitBreaker.canMakeRequest()) {
        console.log("[KarunaCloudServices] Circuit breaker is open, blocking response processing");

        // Navigate to service unavailable page when circuit breaker is open
        if (userLoggedIn && window.location.pathname !== "/serviceunavailable") {
            console.log("[KarunaCloudServices] Navigating to service unavailable page - circuit breaker is open");
            void navigate("/serviceunavailable");
            return false;
        }

        // Show user notification when circuit breaker is blocking requests, but check for existing notification
        // Skip showing standard connection errors for non-logged-in users (Login page handles its own errors)
        if (userLoggedIn) {
            const shouldShowError = circuitBreaker.shouldShowCircuitOpenNotification();
            if (shouldShowError && notificationManager) {
                const notificationId = notificationManager.addError("Server connection unavailable. Please check your connection and refresh the page.");
                circuitBreaker.recordServerUnavailableNotification(notificationId);
            }
        }
        else {
            console.log("[KarunaCloudServices] User not logged in - skipping standard connection error notification (Login page will handle)");
        }

        return false;
    }

    if (!response) {
        return handleCircuitBreakerFailure(
            notificationManager,
            userLoggedIn,
            "Server connection unavailable. Please check your connection and refresh the page.",
            "null response"
        );
    }

    if (response instanceof AxiosError) {
        const [url, status, requestData, reason]: [string, number, string, string] = KarunaCLOUD_parseAxiosError(response);

        // Only trip circuit breaker for connection errors (ERR_CONNECTION_REFUSED, ERR_NETWORK, etc.)
        // Do NOT trip for responses with HTTP status codes (400, 500, etc.) - those mean server responded
        const isConnectionError = !status && (
            reason?.includes("ERR_CONNECTION_REFUSED") ||
            reason?.includes("ERR_NETWORK") ||
            reason?.includes("Network Error") ||
            response.code === "ERR_NETWORK" ||
            response.code === "ERR_CONNECTION_REFUSED"
        );

        if (isConnectionError) {
            return handleCircuitBreakerFailure(
                notificationManager,
                userLoggedIn,
                `Server connection unavailable. Please check your connection and refresh the page.`,
                "AxiosError - connection refused"
            );
        }
        
        if (notificationManager) {
            notificationManager.addError(`Unable to process your request. (${status})\n\n${reason}`);
        }
        return false;
    }

    let status = response.status;
    let code = response.code;
    let data = response.data;
    let error = data?.error ?? data ?? null;

    if (status && status >= 400) {
        let responseCodeStr = `(${status}${code ? `-${code}` : ""})`;
        if (notificationManager) {
            notificationManager.addError(
                `Unable to process your request. Try logging out and back in again. ${responseCodeStr}${error ? `\n\n${JSON.stringify(error)}` : ""}`
            );
        }
        return false;
    }

    circuitBreaker.recordSuccess();
    return true;
};

export const KarunaCLOUD_getAxiosErrorBlock = (error: AxiosError): any[] => {
    const [url, status, requestData, reason]: [string, number, string, string] = KarunaCLOUD_parseAxiosError(error);

    if (url || reason) {
        let axiosErrStr = `${reason ? `${reason}` : ""}${url ? `\n\n${url}${status ? ` (${status})` : ""}` : ""}`;
        return ([
            {
                type: "section",
                text: { type: "mrkdwn", text: `${url || reason ? `\`\`\`${axiosErrStr}\`\`\`` : ""}` }
            }
        ]);
    }

    return [];
};

export const KarunaCLOUD_getStringifiedErrorsBlock = (error: any | any[]): any[] => {
    let stringifiedErrors: string;

    if (Array.isArray(error)) {
        stringifiedErrors = error
            .map(item => item instanceof Error ? item.stack || JSON.stringify(item) : JSON.stringify(item))
            .filter(Boolean)
            .join("");
    }
    else {
        stringifiedErrors = error instanceof Error ? (error.stack || JSON.stringify(error)) : (error ? JSON.stringify(error) : "");
    }

    return ([
        {
            type: "section",
            text: { type: "mrkdwn", text: `\`\`\`${stringifiedErrors}\`\`\`` }
        }
    ]);
};

export const KarunaCLOUD_slackDiagnosticsPrettifier = (error) => {

    let user = cookie.load("user");
    let userInfo = user ? `${user.first_name} ${user.last_name} (${user.role_type}) ${user.email}` : "Unauthenticated User";

    let content_blocks = [
        {
            type: "section",
            text: { type: "mrkdwn", text: `*Time*: ${new Date()} \n *User*: ${userInfo}` }
        }
    ];

    if (error instanceof AxiosError) {
        content_blocks.push(...(KarunaCLOUD_getAxiosErrorBlock(error) ?? []));
    }
    else if (Array.isArray(error) && error.every(err => err instanceof AxiosError)) {
        error.forEach(err => {
            content_blocks.push(...(KarunaCLOUD_getAxiosErrorBlock(err) ?? []));
        });
    }
    else {
        content_blocks.push(...(KarunaCLOUD_getStringifiedErrorsBlock(error) ?? []));
    }

    return content_blocks;
};

// ---==============================================---
